// Hooks de React Query para o domínio CRM Activity (Story 11.8).
// - useCrmActivitiesInfinite — timeline/minhas-tarefas paginada por cursor (DR-032).
// - useCreateCrmActivity — cria atividade/tarefa.
// - useUpdateCrmActivity — edita / conclui tarefa (com conclusão otimista — AC10).
// - useMyPendingTasksCount — contagem de tarefas pendentes pro badge (AC10).
//
// Convenção: queryKey ['crm-activities', 'list', filters].
// Após mutation: invalidateQueries({ queryKey: ['crm-activities'] }).

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import {
  createCrmActivity,
  fetchCrmActivities,
  updateCrmActivity,
  type CrmActivityEntity,
  type CrmActivityListParams,
  type CrmActivityListResponse,
} from '../lib/crm-activities-api';
import type { CreateCrmActivityDto, UpdateCrmActivityDto } from '@control-center/shared';

const DEFAULT_LIMIT = 20;

export const crmActivitiesKeys = {
  all: ['crm-activities'] as const,
  lists: () => [...crmActivitiesKeys.all, 'list'] as const,
  list: (params: Omit<CrmActivityListParams, 'cursor'>) =>
    [...crmActivitiesKeys.lists(), params] as const,
  pendingCount: () => [...crmActivitiesKeys.all, 'pending-count'] as const,
};

export type CrmActivitiesListFilters = Omit<CrmActivityListParams, 'cursor' | 'limit'>;

export function useCrmActivitiesInfinite(filters: CrmActivitiesListFilters = {}) {
  return useInfiniteQuery<
    CrmActivityListResponse,
    Error,
    InfiniteData<CrmActivityListResponse>,
    ReturnType<typeof crmActivitiesKeys.list>,
    string | undefined
  >({
    queryKey: crmActivitiesKeys.list({ ...filters, limit: DEFAULT_LIMIT }),
    queryFn: ({ pageParam, signal }) =>
      fetchCrmActivities({ ...filters, limit: DEFAULT_LIMIT, cursor: pageParam }, signal),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
    staleTime: 30_000,
  });
}

export function useCreateCrmActivity() {
  const qc = useQueryClient();
  return useMutation<CrmActivityEntity, Error, CreateCrmActivityDto>({
    mutationFn: (payload) => createCrmActivity(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: crmActivitiesKeys.all });
    },
  });
}

export interface UpdateCrmActivityArgs {
  id: string;
  payload: UpdateCrmActivityDto;
}

export function useUpdateCrmActivity() {
  const qc = useQueryClient();
  return useMutation<CrmActivityEntity, Error, UpdateCrmActivityArgs>({
    mutationFn: ({ id, payload }) => updateCrmActivity(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: crmActivitiesKeys.all });
    },
  });
}

/**
 * Contagem de tarefas pendentes do usuário logado pro badge do menu (AC10).
 * Reusa o endpoint de lista (my_tasks=true, done=false) e lê `meta.total` —
 * sem endpoint dedicado (KISS). Revalida a cada 60s.
 */
export function useMyPendingTasksCount(enabled = true) {
  return useQuery<number, Error>({
    queryKey: crmActivitiesKeys.pendingCount(),
    queryFn: async ({ signal }) => {
      const res = await fetchCrmActivities({ my_tasks: true, done: false, limit: 1 }, signal);
      return res.meta.total;
    },
    enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}
